import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8a4b748d-a67a-4760-b635-c7f9b7921d7e/artifacts/kn8679bx_Feral%20Ways%20Logo.png";

// Crumbling soil particle - falls and disperses like real soil
class CrumblingParticle {
  constructor(x, y, canvasWidth, canvasHeight, delay = 0) {
    this.x = x;
    this.y = y;
    this.startX = x;
    this.startY = y;
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.delay = delay;
    this.active = delay === 0;
    this.elapsed = 0;
    
    // Varied sizes for realistic soil chunks
    this.size = 4 + Math.random() * 12;
    
    // Movement - mostly falling down with some spread
    this.speedX = (Math.random() - 0.5) * 3;
    this.speedY = Math.random() * 2 + 1;
    this.gravity = 0.15 + Math.random() * 0.1;
    
    // Rotation for tumbling effect
    this.rotation = Math.random() * 360;
    this.rotationSpeed = (Math.random() - 0.5) * 15;
    
    // Fade properties
    this.opacity = 1;
    this.fadeStart = 0.6 + Math.random() * 0.3; // When to start fading (0-1 of lifetime)
    this.maxLife = 80 + Math.random() * 60;
    this.life = 0;
    
    // Rich soil colors
    const colors = [
      [89, 60, 31],    // Dark brown
      [120, 85, 50],   // Medium brown
      [70, 45, 25],    // Deep earth
      [140, 100, 60],  // Light soil
      [100, 70, 40],   // Clay
      [60, 40, 20],    // Dark loam
    ];
    this.color = colors[Math.floor(Math.random() * colors.length)];
    
    // Shape variation
    this.shapeType = Math.floor(Math.random() * 3); // 0=irregular, 1=round, 2=angular
  }

  update(deltaTime = 16) {
    if (!this.active) {
      this.elapsed += deltaTime;
      if (this.elapsed >= this.delay) {
        this.active = true;
      }
      return;
    }
    
    this.life++;
    
    // Apply physics
    this.speedY += this.gravity;
    this.x += this.speedX;
    this.y += this.speedY;
    this.rotation += this.rotationSpeed;
    
    // Air resistance
    this.speedX *= 0.995;
    this.rotationSpeed *= 0.99;
    
    // Fade out based on lifetime progress
    const lifeProgress = this.life / this.maxLife;
    if (lifeProgress > this.fadeStart) {
      const fadeProgress = (lifeProgress - this.fadeStart) / (1 - this.fadeStart);
      this.opacity = 1 - fadeProgress;
    }
  }

  draw(ctx) {
    if (!this.active || this.opacity <= 0) return;
    
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate((this.rotation * Math.PI) / 180);
    ctx.globalAlpha = Math.max(0, this.opacity);
    ctx.fillStyle = `rgb(${this.color[0]}, ${this.color[1]}, ${this.color[2]})`;
    
    // Draw irregular soil chunk shape
    ctx.beginPath();
    if (this.shapeType === 0) {
      // Irregular polygon
      const points = 5 + Math.floor(Math.random() * 3);
      for (let i = 0; i < points; i++) {
        const angle = (i / points) * Math.PI * 2;
        const radius = this.size * (0.5 + Math.random() * 0.5);
        const px = Math.cos(angle) * radius;
        const py = Math.sin(angle) * radius;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
    } else if (this.shapeType === 1) {
      // Rounded clump
      ctx.ellipse(0, 0, this.size, this.size * 0.7, 0, 0, Math.PI * 2);
    } else {
      // Angular fragment
      ctx.moveTo(-this.size * 0.5, -this.size * 0.3);
      ctx.lineTo(this.size * 0.6, -this.size * 0.5);
      ctx.lineTo(this.size * 0.4, this.size * 0.5);
      ctx.lineTo(-this.size * 0.5, this.size * 0.4);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  isDead() {
    return this.active && (this.opacity <= 0 || this.y > this.canvasHeight + 50);
  }
}

// Fine dust particle - lighter, floats more
class DustMote {
  constructor(x, y, canvasHeight) {
    this.x = x;
    this.y = y;
    this.canvasHeight = canvasHeight;
    this.size = 1 + Math.random() * 3;
    
    // Dust floats and drifts
    this.speedX = (Math.random() - 0.5) * 4;
    this.speedY = (Math.random() - 0.3) * 3;
    this.drift = (Math.random() - 0.5) * 0.1;
    
    this.opacity = 0.4 + Math.random() * 0.4;
    this.fadeSpeed = 0.008 + Math.random() * 0.01;
    
    // Warm dust colors
    const r = 140 + Math.random() * 40;
    const g = 100 + Math.random() * 30;
    const b = 60 + Math.random() * 20;
    this.color = `rgba(${r}, ${g}, ${b}`;
  }

  update() {
    this.speedX += this.drift;
    this.x += this.speedX;
    this.y += this.speedY;
    this.speedY += 0.02; // Very light gravity
    this.speedX *= 0.98;
    this.opacity -= this.fadeSpeed;
  }

  draw(ctx) {
    if (this.opacity <= 0) return;
    ctx.save();
    ctx.globalAlpha = Math.max(0, this.opacity);
    ctx.fillStyle = `${this.color}, ${this.opacity})`;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  isDead() {
    return this.opacity <= 0 || this.y > this.canvasHeight + 30;
  }
}

export const SoilWipeIntro = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isDissolving, setIsDissolving] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [textureLoaded, setTextureLoaded] = useState(false);
  
  const mainCanvasRef = useRef(null);
  const particleCanvasRef = useRef(null);
  const textureRef = useRef(null);
  const crumblingParticlesRef = useRef([]);
  const dustMotesRef = useRef([]);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(0);
  const dissolvePhaseRef = useRef(0); // 0-3 for wave phases
  const dimensionsRef = useRef({ width: 0, height: 0, dpr: 1 });
  const isCompleteRef = useRef(false);
  const isDissolvingRef = useRef(false);
  const soilImageDataRef = useRef(null);

  // Create procedural soil texture
  useEffect(() => {
    createSoilTexture();
  }, []);

  useEffect(() => {
    if (textureLoaded) {
      setTimeout(() => setShowContent(true), 400);
    }
  }, [textureLoaded]);

  const createSoilTexture = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 1024;
    const ctx = canvas.getContext('2d');
    
    // Rich brown soil base
    const gradient = ctx.createRadialGradient(512, 512, 0, 512, 512, 700);
    gradient.addColorStop(0, '#5a4030');
    gradient.addColorStop(0.4, '#4a3525');
    gradient.addColorStop(0.7, '#3d2a1a');
    gradient.addColorStop(1, '#2d1f12');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1024, 1024);
    
    // Add layered noise
    const imageData = ctx.getImageData(0, 0, 1024, 1024);
    const data = imageData.data;
    
    for (let i = 0; i < data.length; i += 4) {
      const noise1 = (Math.random() - 0.5) * 35;
      const noise2 = (Math.random() - 0.5) * 20;
      data[i] = Math.min(255, Math.max(0, data[i] + noise1 + noise2));
      data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + noise1 * 0.85 + noise2));
      data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + noise1 * 0.65 + noise2));
    }
    ctx.putImageData(imageData, 0, 0);
    
    // Add soil clumps
    for (let i = 0; i < 400; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 1024;
      const size = 2 + Math.random() * 6;
      const brightness = 30 + Math.random() * 70;
      ctx.fillStyle = `rgba(${brightness + 20}, ${brightness}, ${brightness * 0.6}, ${0.3 + Math.random() * 0.4})`;
      ctx.beginPath();
      ctx.ellipse(x, y, size, size * 0.7, Math.random() * Math.PI, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Add small stones
    for (let i = 0; i < 60; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 1024;
      const size = 3 + Math.random() * 7;
      const gray = 50 + Math.random() * 40;
      ctx.fillStyle = `rgba(${gray}, ${gray * 0.9}, ${gray * 0.8}, ${0.5 + Math.random() * 0.4})`;
      ctx.beginPath();
      ctx.ellipse(x, y, size, size * (0.5 + Math.random() * 0.3), Math.random() * Math.PI, 0, Math.PI * 2);
      ctx.fill();
    }
    
    const img = new Image();
    img.src = canvas.toDataURL();
    img.onload = () => {
      textureRef.current = img;
      setTextureLoaded(true);
    };
  };

  const initCanvas = useCallback(() => {
    const mainCanvas = mainCanvasRef.current;
    const particleCanvas = particleCanvasRef.current;
    
    if (!mainCanvas || !particleCanvas || !textureRef.current) return;

    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;

    dimensionsRef.current = { width, height, dpr };

    [mainCanvas, particleCanvas].forEach(c => {
      c.width = width * dpr;
      c.height = height * dpr;
      c.style.width = `${width}px`;
      c.style.height = `${height}px`;
    });

    const ctx = mainCanvas.getContext('2d');
    ctx.scale(dpr, dpr);
    particleCanvas.getContext('2d').scale(dpr, dpr);

    drawSoilTexture(ctx, width, height);
    animate();
  }, []);

  const drawSoilTexture = (ctx, width, height) => {
    const img = textureRef.current;
    if (!img) return;

    const scale = Math.max(width / img.width, height / img.height) * 1.4;
    const scaledWidth = img.width * scale;
    const scaledHeight = img.height * scale;
    const offsetX = (width - scaledWidth) / 2;
    const offsetY = (height - scaledHeight) / 2;

    ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

    // Vignette
    const gradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, Math.max(width, height) * 0.65);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(0.5, 'rgba(20, 15, 10, 0.15)');
    gradient.addColorStop(1, 'rgba(20, 15, 10, 0.45)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    // Store initial image data for dissolve effect
    soilImageDataRef.current = ctx.getImageData(0, 0, width * dimensionsRef.current.dpr, height * dimensionsRef.current.dpr);
  };

  // Main animation loop
  const animate = useCallback((timestamp) => {
    if (isCompleteRef.current) return;
    
    const deltaTime = lastTimeRef.current ? timestamp - lastTimeRef.current : 16;
    lastTimeRef.current = timestamp;
    
    const mainCanvas = mainCanvasRef.current;
    const particleCanvas = particleCanvasRef.current;
    const { width, height, dpr } = dimensionsRef.current;
    
    if (!particleCanvas || !mainCanvas || width === 0) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }

    const particleCtx = particleCanvas.getContext('2d');

    // Clear particle canvas
    particleCtx.setTransform(1, 0, 0, 1, 0, 0);
    particleCtx.clearRect(0, 0, width * dpr, height * dpr);
    particleCtx.scale(dpr, dpr);

    // Update and draw crumbling particles
    crumblingParticlesRef.current = crumblingParticlesRef.current.filter(p => {
      p.update(deltaTime);
      p.draw(particleCtx);
      return !p.isDead();
    });

    // Update and draw dust motes
    dustMotesRef.current = dustMotesRef.current.filter(p => {
      p.update();
      p.draw(particleCtx);
      return !p.isDead();
    });

    // Check if dissolve is complete
    if (isDissolvingRef.current && 
        crumblingParticlesRef.current.length === 0 && 
        dustMotesRef.current.length === 0 &&
        dissolvePhaseRef.current >= 4) {
      isCompleteRef.current = true;
      setTimeout(() => {
        setIsVisible(false);
        onComplete();
      }, 200);
      return;
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [onComplete]);

  // Create a wave of crumbling particles from a region
  const createCrumblingWave = useCallback((phase) => {
    const { width, height } = dimensionsRef.current;
    const mainCanvas = mainCanvasRef.current;
    if (!mainCanvas) return;
    
    const ctx = mainCanvas.getContext('2d');
    const { dpr } = dimensionsRef.current;
    
    // Divide screen into regions for staggered dissolve
    // Phase 0: center, Phase 1: surrounding, Phase 2: edges, Phase 3: corners
    const gridCols = 20;
    const gridRows = 12;
    const cellWidth = width / gridCols;
    const cellHeight = height / gridRows;
    
    const centerX = width / 2;
    const centerY = height / 2;
    
    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        const cellCenterX = col * cellWidth + cellWidth / 2;
        const cellCenterY = row * cellHeight + cellHeight / 2;
        
        // Calculate distance from center
        const distFromCenter = Math.sqrt(
          Math.pow(cellCenterX - centerX, 2) + 
          Math.pow(cellCenterY - centerY, 2)
        );
        const maxDist = Math.sqrt(Math.pow(width/2, 2) + Math.pow(height/2, 2));
        const normalizedDist = distFromCenter / maxDist;
        
        // Determine which phase this cell belongs to
        let cellPhase;
        if (normalizedDist < 0.25) cellPhase = 0;
        else if (normalizedDist < 0.5) cellPhase = 1;
        else if (normalizedDist < 0.75) cellPhase = 2;
        else cellPhase = 3;
        
        if (cellPhase !== phase) continue;
        
        // Create particles for this cell - more particles for dramatic effect
        const particlesPerCell = 12 + Math.floor(Math.random() * 8);
        const baseDelay = Math.random() * 300;
        
        for (let i = 0; i < particlesPerCell; i++) {
          const px = cellCenterX + (Math.random() - 0.5) * cellWidth;
          const py = cellCenterY + (Math.random() - 0.5) * cellHeight;
          const delay = baseDelay + Math.random() * 200;
          
          crumblingParticlesRef.current.push(
            new CrumblingParticle(px, py, width, height, delay)
          );
          
          // Add more dust motes for atmosphere
          if (Math.random() > 0.4) {
            dustMotesRef.current.push(new DustMote(px, py, height));
          }
        }
        
        // Clear this cell from the main canvas (with soft edges)
        const clearDelay = baseDelay + 100;
        setTimeout(() => {
          const ctx = mainCanvas.getContext('2d');
          ctx.save();
          ctx.globalCompositeOperation = 'destination-out';
          
          // Create soft-edged clear
          const gradient = ctx.createRadialGradient(
            cellCenterX, cellCenterY, 0,
            cellCenterX, cellCenterY, Math.max(cellWidth, cellHeight) * 0.8
          );
          gradient.addColorStop(0, 'rgba(0,0,0,1)');
          gradient.addColorStop(0.7, 'rgba(0,0,0,0.8)');
          gradient.addColorStop(1, 'rgba(0,0,0,0)');
          
          ctx.fillStyle = gradient;
          ctx.fillRect(
            cellCenterX - cellWidth, 
            cellCenterY - cellHeight, 
            cellWidth * 2, 
            cellHeight * 2
          );
          ctx.restore();
        }, clearDelay);
      }
    }
  }, []);

  // Start the dissolve effect
  const startDissolve = useCallback(() => {
    isDissolvingRef.current = true;
    dissolvePhaseRef.current = 0;
    
    // Create waves with delays - more dramatic timing
    const phaseDelays = [0, 500, 1000, 1500];
    
    phaseDelays.forEach((delay, phase) => {
      setTimeout(() => {
        dissolvePhaseRef.current = phase + 1;
        createCrumblingWave(phase);
      }, delay);
    });
    
    // Fallback completion after max animation time
    setTimeout(() => {
      if (!isCompleteRef.current) {
        isCompleteRef.current = true;
        setIsVisible(false);
        onComplete();
      }
    }, 4000);
  }, [createCrumblingWave, onComplete]);

  useEffect(() => {
    if (textureLoaded) {
      initCanvas();
    }
  }, [textureLoaded, initCanvas]);

  useEffect(() => {
    const handleResize = () => {
      if (textureRef.current && !isDissolving) {
        initCanvas();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [initCanvas, isDissolving]);

  const handleClick = () => {
    if (!isDissolving && textureLoaded) {
      setIsDissolving(true);
      startDissolve();
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[10000] overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          data-testid="soil-wipe-intro"
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        >
          {/* Main soil canvas - particles crumble away from this */}
          <canvas
            ref={mainCanvasRef}
            className="absolute inset-0 pointer-events-none"
          />

          {/* Particle effects canvas */}
          <canvas
            ref={particleCanvasRef}
            className="absolute inset-0 pointer-events-none"
          />

          {/* Loading state */}
          {!textureLoaded && (
            <div className="absolute inset-0 bg-[#3d2a1a] flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-[#C4956A] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Branding Content */}
          <AnimatePresence>
            {showContent && !isDissolving && (
              <motion.div
                className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
              >
                {/* Logo */}
                <motion.img
                  src={LOGO_URL}
                  alt="F.E.R.A.Lways Logo"
                  className="w-24 h-24 md:w-32 md:h-32 object-contain mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                />

                {/* Full Form */}
                <motion.p
                  className="text-[#D4A574] text-xs md:text-sm tracking-[0.3em] uppercase mb-6 text-center"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  Forest Ecology Regenerative Agriculture & Livelihoods
                </motion.p>

                {/* Main Title */}
                <motion.h1
                  className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-4 text-center"
                  style={{ fontWeight: 400, letterSpacing: '0.02em' }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.6 }}
                >
                  F.E.R.A.Lways
                </motion.h1>

                {/* Subtitle */}
                <motion.p
                  className="text-white/80 text-lg md:text-xl font-light tracking-wide text-center"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                >
                  Ecological Landscape Design
                </motion.p>

                {/* Click to Unearth */}
                <motion.div
                  className="absolute bottom-16 left-0 right-0 text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.3 }}
                >
                  <motion.p
                    className="text-[#D4A574]/90 text-sm tracking-[0.25em] uppercase"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    Click to Unearth
                  </motion.p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
